// Operator-facing changelog. Each entry is a small story: what changed,
// what problem it solved, why you (the operator) might care.
//
// This is intentionally NOT auto-generated from `RELEASES.md` — the
// release notes are technical and exhaustive; this file is curated and
// problem-first. Add an entry when a deploy ships something operators
// would actually feel.
//
// Conventions:
//   - id: short stable slug, used as the "last seen" key. Don't reuse.
//   - title: one short noun phrase, < 60 chars
//   - problem: 1–2 sentences, plain English, NO internal jargon
//     (no JSONPath, no "MERGE", no "P4", no class names)
//   - details: optional 2–4 bullets for operators who want more
//   - date: YYYY-MM-DD (deploy date, not commit date)
//
// Newest first. The button shows an "unread" badge whenever the latest
// id is newer than `localStorage.mdh_whatsnew_seen`.

export interface WhatsNewEntry {
  id: string;
  date: string;
  title: string;
  problem: string;
  details?: string[];
}

export const WHATS_NEW: WhatsNewEntry[] = [
  {
    id: 'v0.3.0-auto-connect-bq',
    date: '2026-05-09',
    title: 'Drop a tool, get a BigQuery dashboard — automatically',
    problem:
      "Adding a new source used to mean: pick a merge_key, name a table, set up a promote_to_table trigger, wait for events to flow, then point Looker at the result. Now you point your tool at a webhook URL, and once 10 events of any schema arrive, a banner appears with one click that creates a BigQuery external table + view ready for Looker, Sheets, or BQ console. Costs $0 in storage — the view reads the events from GCS on demand.",
    details: [
      'Connector profiles (Pipedrive, Stripe, HubSpot, Treblle) auto-detect the discriminator field — Pipedrive\'s "event", Stripe\'s "type", HubSpot\'s "subscriptionType", Treblle\'s "data.request.method".',
      'Generic webhooks fall through to a heuristic: top-level + 1-deep string fields scored by name (event/type/kind), cardinality (<20 distinct), coverage (>90%).',
      'A banner on the source detail page shows new event_types as they cross 10 events. One click → BQ external table + view + deep-links.',
      'Each event_type gets its own view — "deal.added" and "deal.updated" land as separate queryable surfaces, not one giant blob.',
      'Re-running auto-connect-bq is idempotent: dataset_hash stays stable, so the same view name updates instead of duplicating.',
    ],
  },
  {
    id: 'v0.3.0-source-auto-naming',
    date: '2026-05-09',
    title: 'Sources name themselves from the tool that\'s posting',
    problem:
      "Created a source without a name? It used to land as 'Source 4abc12'. Now we read the User-Agent of the first webhook (or any current one if you forgot) and rename it to the matching tool — 'Pipedrive', 'Stripe', 'HubSpot', etc. The source's connector_profile gets stamped at the same time so the discriminator + suggested merge keys are wired up too.",
    details: [
      'Pipedrive-Webhook UA → name "Pipedrive", connector_profile "pipedrive".',
      'Stripe UA → name "Stripe", connector_profile "stripe".',
      'Falls back to "Source <prefix>" when no profile matches.',
      'Operator can rename anytime via the Settings tab; auto-naming only fires when name was blank at create time.',
    ],
  },
  {
    id: 'v0.2.3-replay-facets',
    date: '2026-05-09',
    title: 'Narrow a replay to just the events you care about',
    problem:
      "Replays used to fan out across every event in the chosen scope and let triggers filter at evaluation time — fine for small sources, expensive on large ones. Now the Replay dialog has metadata facets (depth, origin kind, trigger, tag) that prune the GCS scan up front. Same dry-run-first flow; just a smaller blast radius and a faster run.",
    details: [
      'Open Replay dialog → "Metadata facets" disclosure (closed by default).',
      'Pick depth ≤ N (e.g. "0 = input only" skips trigger derivatives entirely).',
      'Pick origin kind: input or trigger.',
      'Pick a specific trigger to replay only events its version produced.',
      'Filter by tag key=value (e.g. env=prod) — matches the source\'s tag.* metadata.',
      'Active facet count shows on the disclosure header so you don\'t leave a stale filter on by accident.',
    ],
  },
  {
    id: 'v0.2.2-share-upload-url',
    date: '2026-05-09',
    title: 'Drop-in upload URL you can hand to a partner',
    problem:
      "Sharing the per-source /upload URL with a partner used to mean digging through Settings or copying it from the operator upload modal. Now there's a one-stop panel: URL with copy button, live drag-and-drop demo so you can verify it works, and a list of accepted formats with MIME hints.",
    details: [
      'Open via AddInputDialog → "Public upload URL (share + test)" card.',
      'Drag a file onto the panel → POSTs to /upload/:slug → toast shows accepted event count.',
      'Accepted formats: JSON, NDJSON, CSV, XLSX, XLS, ZIP, GZ, TAR, XML, PDF.',
      'Operator file-picker (UploadDialog) still ships behind the hero "upload" button — no change there.',
    ],
  },
  {
    id: 'v0.2.2-per-input-gate',
    date: '2026-05-09',
    title: 'Pause one channel without pausing the whole source',
    problem:
      "Until today, pausing the webhook input had no effect on ingestion — the source-level pause was the only gate. Now you can pause or archive each channel (webhook, upload, email, scheduled pull, manual) independently. Useful when migrating a partner from webhook to email parse without disrupting other partners on the same source.",
    details: [
      'Pause webhook input → POSTs return 503 with Retry-After: 60 (transient).',
      'Archive webhook input → POSTs return 410 (permanent; partner needs to refactor).',
      'Same gate runs on /upload for the file_upload input.',
      'Source-level pause/archive still takes precedence (checked first).',
      'Manage from the AddInputDialog → click any channel card → rename or pause inline.',
    ],
  },
  {
    id: 'v0.2.2-email-panel',
    date: '2026-05-09',
    title: 'Email parse channel has its own panel now',
    problem:
      "The email channel was hidden behind a one-click 'copy address to clipboard' toast. Operators who wanted to share the address with a partner had to dig through the AddInputDialog every time. Now there's a stable dialog showing the address, status, partner-facing usage notes, and an honest roadmap line about MX handler progress.",
    details: [
      'Open via AddInputDialog → "Email parse" card.',
      'Address is shown large + copyable; status pill reflects per-input state.',
      'Inbound MX handler is still being wired — the panel says so plainly so you don\'t expect events from sending mail today.',
    ],
  },
  {
    id: 'v0.2.1-deploys-from-github',
    date: '2026-05-09',
    title: 'Production deploys now come from GitHub `main`, automatically',
    problem:
      "Until today, deploying meant a manual `make deploy` from someone's laptop. That's how live got into a state we didn't intend earlier today (a deploy went out from a worktree that was missing a feature). Now every merge into `main` on GitHub builds + deploys + runs the smoke suite + rolls itself back if smoke fails. Same source of truth for everyone.",
    details: [
      'Workload Identity Federation auth — no service-account keys to leak.',
      'Smoke fails → traffic instantly rolls back to the previous revision; the rollback target is posted in the GitHub Actions job summary.',
      'Manual `make deploy` still works for emergencies but is no longer the normal path.',
      'See INCIDENT_2026-05-09.md for the post-mortem of the regression that prompted this.',
    ],
  },
  {
    id: 'v0.2.0-datasets',
    date: '2026-05-09',
    title: 'Build a dataset from any source without writing SQL',
    problem:
      "You can now point at one or many sources, slice by date / depth / tag, see the live count update as you tweak filters, preview the rows, then export — without setting up a per-source pipeline first.",
    details: [
      'New page at /datasets/new — pick sources, set facets, preview, export.',
      'Each dataset gets a stable hash so you can reproduce the exact same selection later, or hand it to a partner and prove they got identical data.',
      'Export gives you a manifest of signed URLs so the dataset can be any size — no Cloud Run egress limit.',
      'Live count badge tells you how big the dataset will be before you export.',
    ],
  },
  {
    id: 'v0.2.0-replay-history',
    date: '2026-05-09',
    title: "Replay 'everything since' instead of just the last 24h",
    problem:
      "Old replays were limited to the events still in the live preview window (~24h). Now replay walks the full GCS history with metadata facets — depth, trigger version, tags — so you can re-run a year of data through a fixed trigger.",
    details: [
      'New facet panel in the Replay dialog: derivation depth, origin kind, trigger picker, key=value tag filter.',
      'Live "X events match" count appears before you launch.',
      'Lineage drawer survives the 24h Firestore cache — deep history no longer shows "unknown parent".',
    ],
  },
  {
    id: 'v0.2.0-input-rename-pause',
    date: '2026-05-09',
    title: 'Rename and pause individual input channels',
    problem:
      "Each source comes with five channels (webhook, email, upload form, scheduled pull, manual). You can now give each one a friendly name and pause it independently, instead of pausing the whole source.",
    details: [
      'Open + Add input → rename or pause any row inline.',
      'Paused channels return 503 Retry-After so partners retry instead of dropping data.',
      'Webhook URL stays valid even when paused — no integration refactor needed.',
    ],
  },
  {
    id: 'v0.2.0-partner-portal-history',
    date: '2026-05-09',
    title: 'Partner Portal can show the full event history',
    problem:
      "Partners who clicked your portal link only saw the last 24h of events. They can now ask for any date range and see everything, with the same per-source scoping.",
    details: [
      'Pass ?from=… &to=… &depth_max=… on the portal events tab.',
      'Backed by the same fast metadata-only path the dashboard uses, so it stays cheap.',
    ],
  },
  {
    id: 'v0.1.8-secrets-vault',
    date: '2026-04-27',
    title: 'Stop pasting API keys into trigger URLs',
    problem:
      'Triggers can now reference @SECRET_NAME in URLs and headers. The actual value lives in the workspace vault — never in plain text in the trigger config, never in Firestore exports.',
    details: [
      'Workspace → Secrets to add / rotate / revoke.',
      'Triggers picker shows a chip when a secret is in scope.',
      '"Used by N triggers" counter on each secret so you know what breaks if you rotate.',
    ],
  },
  {
    id: 'v0.1.8-data-quality',
    date: '2026-04-27',
    title: "Reject bad events at the door instead of polluting downstream",
    problem:
      "Two new gating triggers (validate_required + validate_dedupe) run before forwarding or promoting. If they fail, the event is quarantined and downstream triggers don't fire — so a partner's typo doesn't end up in your BigQuery table.",
    details: [
      "validate_required: assert specific fields exist and aren't empty.",
      'validate_dedupe: catch repeats inside a sliding window keyed on a payload field.',
      'Failures show inline in the Live tab and the Errors tab — with deep links so you can see the offending event.',
    ],
  },
  {
    id: 'v0.1.8-enrich-fetch',
    date: '2026-04-27',
    title: 'Enrich incoming events with data from another system',
    problem:
      "When a partner sends only an ID, you can now fetch the full record from their API and merge it onto the event before triggers run. The enriched event is what hits your downstream — no separate join required.",
    details: [
      'Pick a field from the payload, point at a URL with {id} placeholder, choose where to merge the response.',
      '10-second timeout, SSRF-protected (no metadata-server reach).',
    ],
  },
  {
    id: 'v0.1.8-slug-rename',
    date: '2026-04-27',
    title: 'Rename your webhook URL without breaking partners',
    problem:
      "Friendly names like /h/pipedrive-prod instead of /h/src_abc123…. When you rename, the old URL keeps working — for the last 10 names, indefinitely. Partners never need to redeploy because of a name change.",
    details: [
      'Click the slug in the URL hero to rename. Live availability check + format hints inline.',
      'Past aliases show as chips below the rename field.',
      '/hook resolves source_id → current alias → any past alias in order.',
    ],
  },
  {
    id: 'v0.1.8-pause-webhook',
    date: '2026-04-27',
    title: 'Pause ingestion during incidents — partners retry, you don\'t lose data',
    problem:
      "Hitting a partner outage or doing a maintenance window? Pause the source. POSTs get 503 with Retry-After: 60 so the partner's retry logic kicks in. Resume when ready — no data lost, no integration changes.",
    details: [
      'Distinct from archive (which 410s permanently). Pause is for "I\'ll be back".',
      'Pause / resume button is the right half of the LIVE pill on the URL hero.',
    ],
  },
  {
    id: 'v0.1.7-per-source-bq-dataset',
    date: '2026-04-15',
    title: 'Each source gets its own BigQuery dataset by default',
    problem:
      "Sources used to land in a shared dataset, which made cross-tenant access hard to audit. Now every source has its own dataset (deterministic name from source_id) — point IAM at it directly.",
    details: [
      'promote_to_table writes here automatically.',
      'Per-trigger override still available for shared / fan-in patterns.',
      'BigQuery rows browser inside the source page so you can verify what\'s actually in the table.',
    ],
  },
  {
    id: 'v0.1.6-replay-everything',
    date: '2026-04-08',
    title: 'Re-run every event you\'ve ever ingested through a fixed trigger',
    problem:
      "When you fix a buggy trigger and want to backfill, you don't have to choose a date range any more — just \"replay everything\" and Datahub walks the entire GCS history.",
    details: [
      'Idempotent: same parent + trigger version → same derivative, MERGE de-dupes.',
      'Cloud Run Job mode for >10-minute replays — no request-timeout cap.',
    ],
  },
];
